import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  ChartLine, MagnifyingGlass, Sparkle, Database, Rocket, Target,
  Check, ArrowRight, Lightning, Eye, Crown, CaretDown, Play,
  ChartBar, Clock, ShieldCheck, Star, Robot, Cursor, Quotes, Globe,
} from '@phosphor-icons/react';
import LeadMagnet from '@/components/LeadMagnet';
import DiscoveryDemo from '@/components/DiscoveryDemo';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { openConsentPreferences } from '@/lib/consent';

const handleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + '/dashboard';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Header = () => {
  const { t } = useTranslation();
  return (
  <header className="fixed top-0 w-full z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 bg-gradient-to-br from-[#2563EB] to-[#3B82F6] rounded-lg flex items-center justify-center">
          <ChartLine size={24} weight="bold" className="text-white" />
        </div>
        <span className="text-xl font-[Outfit] font-bold tracking-tight">FunnelHack AI</span>
      </div>
      <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
        <a href="#features" className="hover:text-white transition">{t('landing.nav_features')}</a>
        <a href="#como-funciona" className="hover:text-white transition">{t('landing.nav_how')}</a>
        <a href="#pricing" className="hover:text-white transition">{t('landing.nav_pricing')}</a>
        <a href="#faq" className="hover:text-white transition">{t('landing.nav_faq')}</a>
        <Link to="/hall-of-winners" className="hover:text-white transition flex items-center gap-1" data-testid="header-hall-link">
          <span>{t('landing.nav_hall')}</span>
          <span className="px-1.5 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B] rounded text-[9px] font-semibold uppercase tracking-wider">{t('landing.nav_hall_badge')}</span>
        </Link>
        <Link to="/guia" className="hover:text-white transition" data-testid="header-guide-link">{t('landing.nav_guide')}</Link>
      </nav>
      <div className="flex items-center gap-3">
        <LanguageSwitcher variant="compact" />
        <button
          onClick={handleLogin}
          data-testid="header-login-btn"
          className="px-5 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition"
        >
          {t('landing.btn_signin')}
        </button>
        <button
          onClick={handleLogin}
          data-testid="header-cta-btn"
          className="hidden sm:inline-flex px-5 py-2 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:opacity-90 rounded-lg text-sm font-semibold transition shadow-lg shadow-blue-500/20"
        >
          {t('landing.btn_trial')}
        </button>
      </div>
    </div>
  </header>
  );
};

const StatPill = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 px-5 py-3 bg-white/[0.03] border border-white/10 rounded-2xl backdrop-blur-xl">
    <div className="w-9 h-9 rounded-lg bg-[#2563EB]/15 flex items-center justify-center">
      <Icon size={18} weight="bold" className="text-[#3B82F6]" />
    </div>
    <div>
      <div className="text-xs uppercase tracking-[0.15em] text-white/40">{label}</div>
      <div className="font-[Outfit] font-bold text-white text-lg">{value}</div>
    </div>
  </div>
);

const Hero = () => {
  const { t } = useTranslation();
  const [proof, setProof] = useState(null);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/stats`)
      .then((r) => setProof(r.data))
      .catch(() => {});
  }, []);

  return (
  <section className="relative pt-36 pb-24 px-6 overflow-hidden">
    {/* radial glow */}
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#2563EB]/20 blur-[180px]" />
    </div>

    <div className="relative max-w-6xl mx-auto text-center">
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full mb-8">
        <Sparkle size={16} weight="fill" className="text-[#2563EB]" />
        <span className="text-xs text-white/80 uppercase tracking-[0.25em]">{t('landing.hero_badge')}</span>
      </div>

      <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-[Outfit] font-bold tracking-tight mb-6 leading-[1.05]">
        {t('landing.hero_title_line1')}<br />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] via-[#60A5FA] to-[#3B82F6]">
          {t('landing.hero_title_line2')}
        </span>
      </h1>

      <p className="text-base md:text-xl text-white/60 max-w-3xl mx-auto mb-10 font-[Manrope] leading-relaxed">
        {t('landing.hero_subtitle')}
      </p>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
        <button
          onClick={handleLogin}
          data-testid="hero-cta-btn"
          className="group px-7 py-3.5 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:opacity-90 rounded-xl text-base font-semibold transition flex items-center gap-2 shadow-2xl shadow-blue-500/30"
        >
          <Rocket size={20} weight="fill" />
          {t('landing.hero_cta_primary')}
          <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
        </button>
        <Link
          to="/guia"
          data-testid="hero-guide-btn"
          className="px-7 py-3.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-base font-medium transition flex items-center gap-2"
        >
          <Play size={18} weight="fill" />
          {t('landing.hero_cta_secondary')}
        </Link>
      </div>

      <div className="text-xs text-white/40 mb-6">
        {t('landing.hero_disclaimer')}
      </div>

      {/* Social proof counter — live analyses this week */}
      {proof && (proof.week_count >= 5 || proof.total_count >= 20) && (
        <div
          data-testid="social-proof-counter"
          className="inline-flex items-center gap-2.5 px-4 py-2 bg-[#10B981]/10 border border-[#10B981]/30 rounded-full mb-12"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]" />
          </span>
          <span className="text-sm text-[#34D399] font-medium">
            {proof.week_count >= 5
              ? t('landing.social_week', { count: proof.week_count })
              : t('landing.social_total', { count: proof.total_count })}
          </span>
        </div>
      )}

      {/* Hero stats */}
      <div className="flex flex-wrap items-center justify-center gap-3 max-w-4xl mx-auto">
        <StatPill icon={Lightning} label={t('landing.stat_ai_label')} value={t('landing.stat_ai_value')} />
        <StatPill icon={Eye} label={t('landing.stat_monitor_label')} value={t('landing.stat_monitor_value')} />
        <StatPill icon={ShieldCheck} label={t('landing.stat_stripe_label')} value={t('landing.stat_stripe_value')} />
        <StatPill icon={Globe} label={t('landing.stat_lang_label')} value={t('landing.stat_lang_value')} />
      </div>
    </div>
  </section>
  );
};

const DemoWindow = () => {
  const { t } = useTranslation();
  return (
  <section className="relative px-6 pb-24">
    <div className="max-w-6xl mx-auto">
      <div className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent overflow-hidden shadow-2xl shadow-blue-500/10">
        {/* fake browser chrome */}
        <div className="flex items-center gap-2 px-5 py-3 bg-black/40 border-b border-white/10">
          <span className="w-3 h-3 rounded-full bg-[#FF5F56]" />
          <span className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
          <span className="w-3 h-3 rounded-full bg-[#27C93F]" />
          <div className="flex-1 mx-4 px-3 py-1 bg-white/5 rounded-md text-xs text-white/40 text-center">
            funnelhack.alemconsciente.com/funnel-spy
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 p-6 md:p-8">
          {/* Left: simulated funnel analysis */}
          <div className="md:col-span-7 space-y-4">
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-1">{t('landing.demo_eyebrow')}</div>
            <div className="text-2xl font-[Outfit] font-bold">{t('landing.demo_title')}</div>

            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="p-4 bg-black/40 border border-white/10 rounded-xl">
                <div className="text-xs text-white/40 uppercase tracking-wide">{t('landing.demo_conv')}</div>
                <div className="text-2xl font-[Outfit] font-bold text-[#10B981]">8.2%</div>
              </div>
              <div className="p-4 bg-black/40 border border-white/10 rounded-xl">
                <div className="text-xs text-white/40 uppercase tracking-wide">{t('landing.demo_ticket')}</div>
                <div className="text-2xl font-[Outfit] font-bold text-white">€47</div>
              </div>
              <div className="p-4 bg-black/40 border border-white/10 rounded-xl">
                <div className="text-xs text-white/40 uppercase tracking-wide">{t('landing.demo_score')}</div>
                <div className="text-2xl font-[Outfit] font-bold text-[#3B82F6]">87/100</div>
              </div>
            </div>

            <div className="p-4 bg-black/40 border border-white/10 rounded-xl">
              <div className="text-xs text-white/40 uppercase tracking-wide mb-2">{t('landing.demo_headline_detected')}</div>
              <div className="text-base text-white/90 italic">&ldquo;{t('landing.demo_headline_text')}&rdquo;</div>
            </div>

            <div className="p-4 bg-black/40 border border-white/10 rounded-xl">
              <div className="text-xs text-white/40 uppercase tracking-wide mb-2">{t('landing.demo_structure')}</div>
              <div className="flex items-center gap-2 text-sm text-white/70 flex-wrap">
                <span className="px-2 py-1 bg-[#2563EB]/15 text-[#3B82F6] rounded">Landing</span>
                <ArrowRight size={14} className="text-white/30" />
                <span className="px-2 py-1 bg-[#2563EB]/15 text-[#3B82F6] rounded">VSL 4min</span>
                <ArrowRight size={14} className="text-white/30" />
                <span className="px-2 py-1 bg-[#2563EB]/15 text-[#3B82F6] rounded">Checkout</span>
                <ArrowRight size={14} className="text-white/30" />
                <span className="px-2 py-1 bg-[#F59E0B]/15 text-[#F59E0B] rounded">Order Bump</span>
                <ArrowRight size={14} className="text-white/30" />
                <span className="px-2 py-1 bg-[#10B981]/15 text-[#10B981] rounded">Upsell</span>
              </div>
            </div>
          </div>

          {/* Right: copy generator */}
          <div className="md:col-span-5 space-y-3">
            <div className="flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-[#3B82F6]" />
              <span className="text-xs uppercase tracking-[0.2em] text-white/40">{t('landing.demo_gen_copy')}</span>
            </div>
            <div className="p-4 bg-black/40 border border-white/10 rounded-xl space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-white/40">Headline #1</div>
                <div className="text-sm text-white/90">&ldquo;{t('landing.demo_h1_text')}&rdquo;</div>
              </div>
              <div className="border-t border-white/5 pt-3">
                <div className="text-[10px] uppercase tracking-wide text-white/40">Headline #2</div>
                <div className="text-sm text-white/90">&ldquo;{t('landing.demo_h2_text')}&rdquo;</div>
              </div>
              <div className="border-t border-white/5 pt-3">
                <div className="text-[10px] uppercase tracking-wide text-white/40">CTA</div>
                <div className="text-sm text-white/90">&ldquo;{t('landing.demo_cta_text')}&rdquo;</div>
              </div>
            </div>
            <div className="px-4 py-3 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-xl text-xs text-[#93C5FD]">
              <Lightning size={14} weight="fill" className="inline mr-1 mb-0.5" />
              {t('landing.demo_generated_in')}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

const Features = () => {
  const { t } = useTranslation();
  const items = [
    {
      icon: MagnifyingGlass,
      color: '#3B82F6',
      title: t('landing.feat_spy_title'),
      desc: t('landing.feat_spy_desc'),
    },
    {
      icon: Sparkle,
      color: '#F59E0B',
      title: t('landing.feat_composer_title'),
      desc: t('landing.feat_composer_desc'),
    },
    {
      icon: Eye,
      color: '#10B981',
      title: t('landing.feat_monitor_title'),
      desc: t('landing.feat_monitor_desc'),
    },
    {
      icon: Database,
      color: '#A855F7',
      title: t('landing.feat_library_title'),
      desc: t('landing.feat_library_desc'),
    },
    {
      icon: ChartBar,
      color: '#EC4899',
      title: t('landing.feat_estimates_title'),
      desc: t('landing.feat_estimates_desc'),
    },
    {
      icon: ShieldCheck,
      color: '#06B6D4',
      title: t('landing.feat_private_title'),
      desc: t('landing.feat_private_desc'),
    },
  ];
  return (
    <section id="features" className="px-6 py-24 border-t border-white/5">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-[0.3em] text-[#3B82F6] mb-3">{t('landing.features_eyebrow')}</div>
          <h2 className="text-4xl md:text-5xl font-[Outfit] font-bold tracking-tight">
            {t('landing.features_title_1')}<br />
            <span className="text-white/60">{t('landing.features_title_2')}</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it) => (
            <div
              key={it.title}
              className="group p-7 bg-white/[0.02] border border-white/10 rounded-2xl hover:border-white/20 hover:bg-white/[0.04] transition"
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition group-hover:scale-110"
                style={{ backgroundColor: `${it.color}22` }}
              >
                <it.icon size={22} weight="bold" style={{ color: it.color }} />
              </div>
              <h3 className="text-xl font-[Outfit] font-bold mb-2">{it.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowItWorks = () => {
  const { t } = useTranslation();
  const steps = [
    {
      n: '01', icon: Cursor, color: '#3B82F6',
      title: t('landing.how_step1_title'),
      desc: t('landing.how_step1_desc'),
    },
    {
      n: '02', icon: Robot, color: '#A855F7',
      title: t('landing.how_step2_title'),
      desc: t('landing.how_step2_desc'),
    },
    {
      n: '03', icon: Sparkle, color: '#F59E0B',
      title: t('landing.how_step3_title'),
      desc: t('landing.how_step3_desc'),
    },
    {
      n: '04', icon: Eye, color: '#10B981',
      title: t('landing.how_step4_title'),
      desc: t('landing.how_step4_desc'),
    },
  ];
  return (
    <section id="como-funciona" className="px-6 py-24 border-t border-white/5 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] rounded-full bg-purple-500/10 blur-[150px]" />
        <div className="absolute top-1/2 -right-40 w-[500px] h-[500px] rounded-full bg-blue-500/10 blur-[150px]" />
      </div>
      <div className="relative max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="text-xs uppercase tracking-[0.3em] text-[#3B82F6] mb-3">{t('landing.how_eyebrow')}</div>
          <h2 className="text-4xl md:text-5xl font-[Outfit] font-bold tracking-tight">
            {t('landing.how_title_line1')}<br />
            <span className="text-white/60">{t('landing.how_title_line2')}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((s, idx) => (
            <div key={s.n} className="relative">
              <div className="p-6 bg-[#0A0A0A] border border-white/10 rounded-2xl h-full">
                <div className="text-[11px] tracking-[0.3em] font-[Outfit] text-white/30 mb-4">{t('landing.how_step')} {s.n}</div>
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: `${s.color}22` }}
                >
                  <s.icon size={26} weight="bold" style={{ color: s.color }} />
                </div>
                <h3 className="text-lg font-[Outfit] font-bold mb-2">{s.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{s.desc}</p>
              </div>
              {idx < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-2 -translate-y-1/2 z-10">
                  <ArrowRight size={18} className="text-white/20" weight="bold" />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/guia"
            data-testid="how-it-works-guide-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm font-medium transition"
          >
            {t('landing.how_full_guide')}
            <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const { t } = useTranslation();
  return (
  <section className="px-6 py-24 border-t border-white/5">
    <div className="max-w-5xl mx-auto text-center">
      <div className="text-xs uppercase tracking-[0.3em] text-[#3B82F6] mb-3">{t('landing.testimonials_eyebrow')}</div>
      <h2 className="text-3xl md:text-4xl font-[Outfit] font-bold tracking-tight mb-6">
        {t('landing.testimonials_title')}
      </h2>
      <p className="text-white/60 max-w-2xl mx-auto mb-10">
        Acabámos de lançar. Sê dos primeiros e a tua história será destacada aqui — descontos vitalícios para fundadores.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="relative p-7 bg-white/[0.02] border border-dashed border-white/15 rounded-2xl text-left flex flex-col"
          >
            <Quotes size={28} weight="fill" className="text-[#2563EB]/30 mb-4" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-3/4 bg-white/5 rounded animate-pulse" />
              <div className="h-3 w-full bg-white/5 rounded animate-pulse" />
              <div className="h-3 w-5/6 bg-white/5 rounded animate-pulse" />
            </div>
            <div className="mt-6 flex items-center gap-3 pt-4 border-t border-white/5">
              <div className="w-10 h-10 rounded-full bg-white/5" />
              <div>
                <div className="h-3 w-20 bg-white/10 rounded mb-1.5" />
                <div className="h-2 w-14 bg-white/5 rounded" />
              </div>
            </div>
            <div className="absolute top-4 right-4 px-2 py-1 bg-[#F59E0B]/15 border border-[#F59E0B]/30 rounded text-[10px] tracking-wider text-[#F59E0B] font-semibold uppercase">
              Em breve
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={handleLogin}
        data-testid="testimonials-cta-btn"
        className="mt-10 inline-flex items-center gap-2 px-6 py-3 bg-[#F59E0B]/15 hover:bg-[#F59E0B]/25 border border-[#F59E0B]/30 rounded-xl text-sm font-medium text-[#F59E0B] transition"
      >
        <Star size={16} weight="fill" />
        Quero ser dos primeiros
      </button>
    </div>
  </section>
  );
};

const Pricing = () => {
  const { t } = useTranslation();
  return (
  <section id="pricing" className="px-6 py-24 border-t border-white/5">
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-16">
        <div className="text-xs uppercase tracking-[0.3em] text-[#3B82F6] mb-3">{t('landing.pricing_eyebrow')}</div>
        <h2 className="text-4xl md:text-5xl font-[Outfit] font-bold tracking-tight">
          {t('landing.pricing_title_line1')}<br />
          <span className="text-white/60">{t('landing.pricing_title_line2')}</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* Pro */}
        <div className="relative p-8 bg-gradient-to-br from-[#2563EB]/15 to-transparent border border-[#2563EB]/40 rounded-2xl">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#2563EB] rounded-full text-[10px] tracking-[0.2em] font-semibold uppercase">
            {t('landing.pricing_most_popular')}
          </div>
          <div className="flex items-center gap-3 mb-5">
            <Rocket size={28} weight="bold" className="text-[#3B82F6]" />
            <span className="text-2xl font-[Outfit] font-bold">Pro</span>
          </div>
          <div className="mb-2">
            <span className="text-5xl font-[Outfit] font-bold">€27</span>
            <span className="text-white/40 ml-2">{t('landing.pricing_per_month')}</span>
          </div>
          <div className="text-xs text-[#10B981] font-medium uppercase tracking-[0.15em] mb-6">
            {t('landing.pricing_trial_note')}
          </div>
          <ul className="space-y-3 mb-8 text-sm text-white/80">
            {[t('landing.pricing_pro_f1'), t('landing.pricing_pro_f2'), t('landing.pricing_pro_f3'), t('landing.pricing_pro_f4'), t('landing.pricing_pro_f5'), t('landing.pricing_pro_f6')].map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check size={18} weight="bold" className="text-[#10B981] flex-shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={handleLogin}
            data-testid="pricing-pro-cta-btn"
            className="w-full px-6 py-3 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] rounded-xl font-semibold transition hover:opacity-90 shadow-lg shadow-blue-500/30"
          >
            {t('landing.pricing_cta')}
          </button>
        </div>

        {/* Agency */}
        <div className="p-8 bg-[#121212] border border-white/10 rounded-2xl">
          <div className="flex items-center gap-3 mb-5">
            <Crown size={28} weight="bold" className="text-[#F59E0B]" />
            <span className="text-2xl font-[Outfit] font-bold">Agency</span>
          </div>
          <div className="mb-2">
            <span className="text-5xl font-[Outfit] font-bold">€97</span>
            <span className="text-white/40 ml-2">{t('landing.pricing_per_month')}</span>
          </div>
          <div className="text-xs text-[#10B981] font-medium uppercase tracking-[0.15em] mb-6">
            {t('landing.pricing_trial_note')}
          </div>
          <ul className="space-y-3 mb-8 text-sm text-white/80">
            {[t('landing.pricing_agency_f1'), t('landing.pricing_agency_f2'), t('landing.pricing_agency_f3'), t('landing.pricing_agency_f4'), t('landing.pricing_agency_f5'), t('landing.pricing_agency_f6'), t('landing.pricing_agency_f7')].map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check size={18} weight="bold" className="text-[#10B981] flex-shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={handleLogin}
            data-testid="pricing-agency-cta-btn"
            className="w-full px-6 py-3 bg-white/10 hover:bg-white/15 rounded-xl font-semibold transition"
          >
            {t('landing.pricing_cta')}
          </button>
        </div>
      </div>
    </div>
  </section>
  );
};

const FAQItem = ({ q, a, slug }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`faq-item-${slug || q.slice(0, 12).toLowerCase().replace(/\s/g, '-')}`}
        className="w-full flex items-center justify-between py-5 text-left transition"
      >
        <span className="text-base md:text-lg font-medium text-white pr-6">{q}</span>
        <CaretDown
          size={20}
          weight="bold"
          className={`flex-shrink-0 text-white/60 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <div className="pb-5 text-white/60 leading-relaxed text-sm md:text-base">{a}</div>}
    </div>
  );
};

const FAQ = () => {
  const { t } = useTranslation();
  return (
  <section id="faq" className="px-6 py-24 border-t border-white/5">
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-12">
        <div className="text-xs uppercase tracking-[0.3em] text-[#3B82F6] mb-3">{t('landing.faq_eyebrow')}</div>
        <h2 className="text-3xl md:text-4xl font-[Outfit] font-bold tracking-tight">{t('landing.faq_title')}</h2>
      </div>
      <div className="space-y-1">
        <FAQItem slug="trial" q={t('landing.faq_q1')} a={t('landing.faq_a1')} />
        <FAQItem slug="cancel" q={t('landing.faq_q2')} a={t('landing.faq_a2')} />
        <FAQItem slug="accuracy" q={t('landing.faq_q3')} a={t('landing.faq_a3')} />
        <FAQItem slug="privacy" q={t('landing.faq_q4')} a={t('landing.faq_a4')} />
        <FAQItem slug="funnels" q={t('landing.faq_q5')} a={t('landing.faq_a5')} />
        <FAQItem slug="ai-tech" q={t('landing.faq_q6')} a={t('landing.faq_a6')} />
        <FAQItem slug="pricing" q={t('landing.faq_q7')} a={t('landing.faq_a7')} />
      </div>
    </div>
  </section>
  );
};

const FinalCTA = () => {
  const { t } = useTranslation();
  return (
  <section className="px-6 py-24 border-t border-white/5">
    <div className="max-w-3xl mx-auto text-center">
      <h2 className="text-4xl md:text-6xl font-[Outfit] font-bold tracking-tight mb-6 leading-[1.1]">
        {t('landing.final_title_line1')}<br />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#3B82F6]">
          {t('landing.final_title_line2')}
        </span>
      </h2>
      <p className="text-white/60 mb-10 text-base md:text-lg max-w-2xl mx-auto">
        {t('landing.final_subtitle')}
      </p>
      <button
        onClick={handleLogin}
        data-testid="final-cta-btn"
        className="group inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:opacity-90 rounded-xl text-base font-semibold transition shadow-2xl shadow-blue-500/30"
      >
        <Rocket size={20} weight="fill" />
        {t('landing.final_cta')}
        <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
      </button>
    </div>
  </section>
  );
};

const Footer = () => {
  const { t } = useTranslation();
  return (
  <footer className="px-6 py-10 border-t border-white/10 text-sm text-white/40">
    <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-gradient-to-br from-[#2563EB] to-[#3B82F6] rounded-lg flex items-center justify-center">
          <ChartLine size={18} weight="bold" className="text-white" />
        </div>
        <span className="text-white/80 font-[Outfit] font-bold">FunnelHack AI</span>
      </div>
      <div className="flex items-center gap-6 flex-wrap justify-center">
        <Link to="/guia" className="hover:text-white transition">{t('landing.footer_guide')}</Link>
        <Link to="/hall-of-winners" className="hover:text-white transition">{t('landing.footer_hall')}</Link>
        <a href="#faq" className="hover:text-white transition">{t('landing.footer_faq')}</a>
        <a href="#pricing" className="hover:text-white transition">{t('landing.footer_pricing')}</a>
        <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition">{t('landing.footer_privacy')}</Link>
        <Link to="/cookies" data-testid="footer-cookies-link" className="hover:text-white transition">{t('landing.footer_cookies')}</Link>
        <button onClick={openConsentPreferences} data-testid="footer-manage-cookies-btn" className="hover:text-white transition">{t('landing.footer_manage_cookies')}</button>
      </div>
      <div>© {new Date().getFullYear()} FunnelHack AI</div>
    </div>
  </footer>
  );
};

const LandingPage = () => {
  const { i18n } = useTranslation();
  useEffect(() => {
    const lng = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2);
    document.title = lng === 'pt'
      ? 'FunnelHack AI — Funnel Hacking inteligente com IA'
      : 'FunnelHack AI — AI-powered funnel hacking platform';
  }, [i18n.resolvedLanguage, i18n.language]);

  // Affiliate ref capture — stash for ProtectedRoute to claim after signup.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref && /^[a-z0-9-]{3,32}$/.test(ref.trim().toLowerCase())) {
        localStorage.setItem('fh_affiliate_ref', ref.trim().toLowerCase());
      }
    } catch { /* private-mode / no storage — non-blocking */ }
  }, []);
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="landing-page">
      <Header />
      <Hero />
      <DemoWindow />
      <DiscoveryDemo />
      <Features />
      <HowItWorks />
      <Testimonials />
      <LeadMagnet />
      <Pricing />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
};

export default LandingPage;
